import { env } from "@/lib/env";
import { resolveTenant, isTenantNotFound } from "@/api/auth";
import type {
  CognitoConfig,
  TenantContext,
  TenantResolveResponse,
} from "@/types/auth";

export interface ResolvedTenant {
  cognitoConfig: CognitoConfig;
  tenant: TenantContext | null;
}

export type TenantResolutionError =
  | { type: "not_found"; slug: string }
  | { type: "network"; message: string }
  | { type: "missing_slug" };

type TenantResolutionResult =
  | { ok: true; data: ResolvedTenant }
  | { ok: false; error: TenantResolutionError };

function extractSlug(hostname: string): {
  shell: "admin" | "patient" | "tenant";
  slug: string | null;
} {
  if (env.isDev && (hostname === "localhost" || hostname === "127.0.0.1")) {
    const devShell = env.devShell;
    if (devShell === "admin") {
      return { shell: "admin", slug: null };
    }
    if (devShell === "patient") {
      return { shell: "patient", slug: null };
    }
    const devSlug = env.devSlug;
    if (devSlug) {
      return { shell: "tenant", slug: devSlug };
    }
    return { shell: "tenant", slug: null };
  }

  const parts = hostname.split(".");
  const subdomain = parts[0];

  if (subdomain === "admin") {
    return { shell: "admin", slug: null };
  }
  if (subdomain === "my") {
    return { shell: "patient", slug: null };
  }

  return { shell: "tenant", slug: subdomain ?? null };
}

function buildAdminConfig(): ResolvedTenant {
  return {
    cognitoConfig: {
      userPoolId: env.superadminPoolId,
      clientId: env.superadminClientId,
      region: env.cognitoRegion,
      mfaRequired: true,
    },
    tenant: null,
  };
}

function buildPatientConfig(): ResolvedTenant {
  return {
    cognitoConfig: {
      userPoolId: env.patientPoolId,
      clientId: env.patientClientId,
      region: env.cognitoRegion,
      mfaRequired: false,
    },
    tenant: null,
  };
}

function buildTenantConfig(response: TenantResolveResponse): ResolvedTenant {
  return {
    cognitoConfig: response.cognitoConfig,
    tenant: {
      tenantId: response.tenantId,
      tenantName: response.tenantName,
      tenantType: response.tenantType,
      branding: response.branding,
    },
  };
}

export async function resolveTenantConfig(
  hostname: string,
): Promise<TenantResolutionResult> {
  const { shell, slug } = extractSlug(hostname);

  if (shell === "admin") {
    return { ok: true, data: buildAdminConfig() };
  }

  if (shell === "patient") {
    return { ok: true, data: buildPatientConfig() };
  }

  if (!slug) {
    return { ok: false, error: { type: "missing_slug" } };
  }

  try {
    const response = await resolveTenant(slug);
    return { ok: true, data: buildTenantConfig(response) };
  } catch (error) {
    if (isTenantNotFound(error)) {
      return { ok: false, error: { type: "not_found", slug } };
    }
    return {
      ok: false,
      error: {
        type: "network",
        message: error instanceof Error ? error.message : String(error),
      },
    };
  }
}
